/**
 * ログインフォーム。
 *
 * - email + password で Cognito User Pool にサインインする。
 * - ログイン成功後は `useAuth` 経由で AuthProvider state が更新され、
 *   App.tsx 側のガードによって BroadcastPointsPage が表示される。
 * - サインアップ画面 (`/signup`) からの遷移時は、確認済みメールアドレスを
 *   location.state で受け取り、初期値として埋める。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';

import { APP_TITLE } from '../constants/appInfo';

import { useAuth } from './AuthProvider';

interface SignUpRedirectState {
  signedUpEmail?: string;
  /** パスワードリセット完了後にリセット済みメールアドレスを受け取る */
  resetEmail?: string;
}

export function LoginPage(): React.JSX.Element {
  const { signIn, error: authError, isLoading } = useAuth();
  const location = useLocation();
  const redirectState = location.state as SignUpRedirectState | null;
  const stateEmail = redirectState?.signedUpEmail;
  const resetEmail = redirectState?.resetEmail;
  const initialEmail = stateEmail ?? resetEmail ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState<string | null>(
    stateEmail
      ? 'メールアドレスの確認が完了しました。作成したパスワードでログインしてください。'
      : resetEmail
        ? 'パスワードの再設定が完了しました。新しいパスワードでログインしてください。'
        : null,
  );

  // location.state が変化したら同期する (サインアップ / パスワードリセット完了直後を想定)
  useEffect(() => {
    if (stateEmail) {
      setEmail(stateEmail);
      setInfo('メールアドレスの確認が完了しました。作成したパスワードでログインしてください。');
    } else if (resetEmail) {
      setEmail(resetEmail);
      setInfo('パスワードの再設定が完了しました。新しいパスワードでログインしてください。');
    }
  }, [stateEmail, resetEmail]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setInfo(null);
    try {
      await signIn(email, password);
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <h1>{APP_TITLE}</h1>
        <form onSubmit={handleSubmit}>
          <label>
            <span>メールアドレス</span>
            <input
              type="email"
              data-testid="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting || isLoading}
            />
          </label>
          <label>
            <span>パスワード</span>
            <input
              type="password"
              data-testid="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting || isLoading}
            />
          </label>
          {info ? <div className="info-message">{info}</div> : null}
          {authError ? <div className="error-message">{authError}</div> : null}
          <button
            type="submit"
            data-testid="login-submit"
            className="primary"
            disabled={submitting || isLoading || !email || !password}
          >
            {submitting ? 'ログイン中...' : 'ログイン'}
          </button>
          <div className="login-footer">
            アカウントをお持ちでない方は <Link to="/signup">新規登録</Link>
          </div>
          <div className="login-footer">
            パスワードをお忘れの方は <Link to="/reset-password">パスワードをリセット</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
